import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import {
    ArrowLeft,
    Calendar,
    Eye,
    Loader2,
    FileText
} from "lucide-react";

interface Article {
    id: string;
    title: string;
    title_en: string | null;
    slug: string;
    excerpt: string | null;
    excerpt_en: string | null;
    content: string;
    content_en: string | null;
    cover_image: string | null;
    published_at: string | null;
    created_at: string;
    view_count: number;
}

const ArticlePage = () => {
    const { id } = useParams();
    const { t, language } = useLanguage();
    const [article, setArticle] = useState<Article | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        const fetchArticle = async () => {
            if (!id) return;

            setIsLoading(true);
            try {
                const { data, error } = await (supabase as any)
                    .from("articles")
                    .select("*")
                    .eq("id", id)
                    .eq("is_published", true)
                    .single();

                if (error) {
                    if (error.code === "PGRST116") {
                        setNotFound(true);
                    } else {
                        throw error;
                    }
                } else {
                    setArticle(data);
                    // Increment view count
                    await (supabase as any).rpc("increment_article_views", { article_id: data.id });
                }
            } catch (err) {
                console.error("Error fetching article:", err);
                setNotFound(true);
            } finally {
                setIsLoading(false);
            }
        };

        fetchArticle();
    }, [id]);

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString(language === "th" ? "th-TH" : "en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    // Get content based on language
    const getTitle = () => {
        if (language === "en" && article?.title_en) return article.title_en;
        return article?.title || "";
    };

    const getContent = () => {
        if (language === "en" && article?.content_en) return article.content_en;
        return article?.content || "";
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-background">
                <Navbar />
                <div className="pt-24 pb-16 flex items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            </div>
        );
    }

    if (notFound || !article) {
        return (
            <div className="min-h-screen bg-background">
                <Navbar />
                <main className="pt-24 pb-16">
                    <div className="container mx-auto px-4 max-w-3xl text-center">
                        <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                        <h1 className="text-2xl font-bold mb-2">{t("ไม่พบบทความ", "Article Not Found")}</h1>
                        <p className="text-muted-foreground mb-6">
                            {t("บทความที่คุณกำลังมองหาอาจถูกลบหรือไม่ได้เผยแพร่", "The article you're looking for may have been removed or is not published.")}
                        </p>
                        <Link to="/dashboard">
                            <Button variant="outline" className="gap-2">
                                <ArrowLeft className="h-4 w-4" />
                                {t("กลับหน้าหลัก", "Back to Dashboard")}
                            </Button>
                        </Link>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            <Navbar />

            <main className="pt-24 pb-16">
                <article className="container mx-auto px-4 max-w-4xl">
                    {/* Back Button */}
                    <Link to="/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6">
                        <ArrowLeft className="h-4 w-4" />
                        {t("กลับหน้า Dashboard", "Back to Dashboard")}
                    </Link>

                    {/* Cover Image */}
                    {article.cover_image && (
                        <div className="rounded-2xl overflow-hidden mb-8 shadow-xl">
                            <img
                                src={article.cover_image}
                                alt={getTitle()}
                                className="w-full h-auto max-h-[400px] object-cover"
                            />
                        </div>
                    )}

                    {/* Header */}
                    <header className="mb-8">
                        <h1 className="text-3xl md:text-4xl font-heading font-bold mb-4">
                            {getTitle()}
                        </h1>

                        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                            {article.published_at && (
                                <div className="flex items-center gap-1">
                                    <Calendar className="h-4 w-4" />
                                    {formatDate(article.published_at)}
                                </div>
                            )}
                            <div className="flex items-center gap-1">
                                <Eye className="h-4 w-4" />
                                {article.view_count} {t("ครั้ง", "views")}
                            </div>
                        </div>
                    </header>

                    {/* Content */}
                    <Card variant="glass" className="rounded-2xl">
                        <CardContent className="p-6 md:p-10">
                            <div
                                className="prose prose-lg dark:prose-invert max-w-none
                                    prose-headings:font-heading prose-headings:font-bold
                                    prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                                    prose-img:rounded-xl prose-img:shadow-lg
                                    prose-blockquote:border-l-primary prose-blockquote:bg-muted/30 prose-blockquote:py-1 prose-blockquote:rounded-r-lg
                                    prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:rounded
                                    prose-pre:bg-muted prose-pre:border prose-pre:border-border"
                                dangerouslySetInnerHTML={{ __html: getContent() }}
                            />
                        </CardContent>
                    </Card>

                    {/* Footer */}
                    <div className="mt-8 pt-8 border-t border-border">
                        <Link to="/dashboard">
                            <Button variant="outline" className="gap-2">
                                <ArrowLeft className="h-4 w-4" />
                                {t("กลับหน้า Dashboard", "Back to Dashboard")}
                            </Button>
                        </Link>
                    </div>
                </article>
            </main>
        </div>
    );
};

export default ArticlePage;
